import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { sendEmail } from '../services/emailService';

interface UploadStep {
  id: number;
  title: string;
  status: 'pending' | 'active' | 'completed' | 'error';
}

interface BusinessRoute {
  id: string;
  businessName: string;
  slug: string;
  createdAt: string;
  uploadCount: number;
  totalSize: number;
  files: UploadedFile[];
  password?: string;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  uploaderEmail: string;
  storageKey: string;
  dataUrl?: string;
  type?: string;
}

const BusinessUploadPortal: React.FC = () => {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const [businessRoute, setBusinessRoute] = useState<BusinessRoute | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [isValidLink, setIsValidLink] = useState<boolean | null>(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploaderName, setUploaderName] = useState('');
  const [uploaderEmail, setUploaderEmail] = useState('');
  const [uploaderMessage, setUploaderMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSteps, setUploadSteps] = useState<UploadStep[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [showFiles, setShowFiles] = useState(false);

  // Load business info from interactive component's localStorage
  useEffect(() => {
    if (businessSlug) {
      // Check interactive component's localStorage
      const savedRoutes = JSON.parse(localStorage.getItem('interactive_business_routes') || '[]');
      const route = savedRoutes.find((r: any) => r.slug === businessSlug);

      if (route) {
        setBusinessRoute(route);
        setBusinessName(route.businessName);
        setIsValidLink(true);
        setRequiresPassword(!!route.password);
        if (!route.password) {
          setAuthenticated(true);
        }
      } else {
        // Fallback to old localStorage for backward compatibility
        const savedLinks = JSON.parse(localStorage.getItem('businessLinks') || '[]');
        const link = savedLinks.find((l: any) => l.slug === businessSlug);

        if (link) {
          setBusinessName(link.businessName);
          setIsValidLink(true);
          setRequiresPassword(!!link.password);
          if (!link.password) {
            setAuthenticated(true);
          }
        } else {
          setIsValidLink(false);
        }
      }
    }
  }, [businessSlug]);

  // Handle password verification
  const verifyPassword = () => {
    if (businessRoute && businessRoute.password === password) {
      setAuthenticated(true);
    } else {
      const savedLinks = JSON.parse(localStorage.getItem('businessLinks') || '[]');
      const link = savedLinks.find((l: any) => l.slug === businessSlug);

      if (link && link.password === password) {
        setAuthenticated(true);
      } else {
        alert('Incorrect password');
      }
    }
  };

  // Download file
  const downloadFile = (file: UploadedFile) => {
    if (!file.dataUrl) {
      alert('File data not available for download');
      return;
    }

    const link = document.createElement('a');
    link.href = file.dataUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  // Remove file
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !uploaderEmail) {
      alert('Please select files and enter your email');
      return;
    }

    setUploading(true);
    setUploadSteps([
      { id: 1, title: 'Validating files...', status: 'active' },
      { id: 2, title: 'Preparing upload...', status: 'pending' },
      { id: 3, title: 'Uploading to cloud storage...', status: 'pending' },
      { id: 4, title: 'Sending notification...', status: 'pending' },
      { id: 5, title: 'Complete!', status: 'pending' },
    ]);

    // Simulate upload process
    for (let i = 0; i < 5; i++) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setUploadSteps(prev => prev.map((step, idx) => ({
        ...step,
        status: idx < i ? 'completed' : idx === i ? 'completed' : idx === i + 1 ? 'active' : 'pending'
      })));
      setUploadedCount(Math.floor((i + 1) / 5 * selectedFiles.length));
    }

    // Update the interactive component's localStorage
    if (businessRoute) {
      const savedRoutes = JSON.parse(localStorage.getItem('interactive_business_routes') || '[]');

      // Process files and create data URLs
      const newFiles = await Promise.all(selectedFiles.map(async (file) => {
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        return {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          uploaderEmail,
          storageKey: `business/${businessSlug}/${Date.now()}_${file.name}`,
          dataUrl,
          type: file.type
        };
      }));

      const updatedRoutes = savedRoutes.map((r: any) => {
        if (r.slug === businessSlug) {
          return {
            ...r,
            uploadCount: (r.uploadCount || 0) + selectedFiles.length,
            totalSize: (r.totalSize || 0) + selectedFiles.reduce((sum, f) => sum + f.size, 0),
            files: [...(r.files || []), ...newFiles]
          };
        }
        return r;
      });
      localStorage.setItem('interactive_business_routes', JSON.stringify(updatedRoutes));

      // Update local state
      const updatedRoute = updatedRoutes.find((r: any) => r.slug === businessSlug);
      if (updatedRoute) {
        setBusinessRoute(updatedRoute);
      }
    }

    // Also save to old format for backward compatibility
    const uploads = JSON.parse(localStorage.getItem('businessUploads') || '{}');
    if (!uploads[businessSlug!]) {
      uploads[businessSlug!] = [];
    }
    uploads[businessSlug!].push({
      files: selectedFiles.map(f => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })),
      uploaderName,
      uploaderEmail,
      uploaderMessage,
      uploadedAt: new Date().toISOString(),
    });
    localStorage.setItem('businessUploads', JSON.stringify(uploads));

    // Send email notification to business owner
    const businessOwnerEmail = localStorage.getItem(`business_owner_${businessSlug}`) || 'b@biig.dev';
    const fileList = selectedFiles.map(f => `• ${f.name} (${(f.size / 1024).toFixed(1)} KB)`).join('\n');

    sendEmail({
      to: businessOwnerEmail,
      subject: `New Files Uploaded to ${businessName}`,
      body: `New files have been uploaded to your business portal!\n\n📤 Uploaded by: ${uploaderName} (${uploaderEmail})\n📁 Files:\n${fileList}\n${uploaderMessage ? `\n💬 Message:\n${uploaderMessage}` : ''}\n\n📍 View uploads at:\nhttps://happyhumantools.com/business-files-interactive\n\nBest regards,\nHappyHumanTools`,
    }).then(result => {
      if (result.success) {
        console.log('Notification email sent to business owner');
      } else {
        console.error('Failed to send notification:', result.error);
      }
    }).catch(error => {
      console.error('Error sending notification:', error);
    });

    setUploadComplete(true);
    setUploading(false);
  };

  // Invalid link
  if (isValidLink === false) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔗</div>
          <h1 className="text-3xl font-bold mb-2">Link Not Found</h1>
          <p className="text-gray-400">This business upload link doesn't exist</p>
        </div>
      </div>
    );
  }

  // Loading
  if (isValidLink === null) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p>Checking link...</p>
        </div>
      </div>
    );
  }

  // Password required
  if (requiresPassword && !authenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-gray-200 rounded-2xl p-8 max-w-md w-full shadow-sm"
        >
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">🔒</div>
            <h1 className="text-2xl font-bold mb-2 text-gray-900">{businessName}</h1>
            <p className="text-gray-600">This upload link is password protected</p>
          </div>

          <div className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-white text-gray-900 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && verifyPassword()}
            />
            <button
              onClick={verifyPassword}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
            >
              Continue
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Upload complete
  if (uploadComplete) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="text-8xl mb-6"
          >
            ✅
          </motion.div>
          <h1 className="text-4xl font-bold mb-4 text-gray-900">Upload Successful!</h1>
          <p className="text-xl text-gray-700 mb-2">Your files have been sent to {businessName}</p>
          <p className="text-gray-600">{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} uploaded successfully</p>

          <button
            onClick={() => window.location.reload()}
            className="mt-8 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
          >
            Upload More Files
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 mt-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-gray-900">
            {businessName}
          </h1>
          <p className="text-xl text-gray-600">
            File Upload Portal
          </p>
          {businessRoute && businessRoute.files && businessRoute.files.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowFiles(!showFiles)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition"
              >
                {showFiles ? 'Hide' : 'Show'} Uploaded Files ({businessRoute.files.length})
              </button>
            </div>
          )}
        </motion.div>

        {/* Files Gallery */}
        {showFiles && businessRoute && businessRoute.files && businessRoute.files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-gray-700"
          >
            <h2 className="text-2xl font-bold mb-4">📁 Uploaded Files</h2>
            <div className="space-y-3">
              {businessRoute.files.map((file: UploadedFile) => (
                <div key={file.id} className="bg-gray-900 rounded-lg p-4 hover:bg-gray-850 transition">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {file.type?.includes('image') ? '🖼️' :
                         file.type?.includes('pdf') ? '📄' :
                         file.type?.includes('video') ? '🎥' : '📎'}
                      </span>
                      <div>
                        <div className="font-medium">{file.name}</div>
                        <div className="text-sm text-gray-400">
                          {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploadedAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">From: {file.uploaderEmail}</div>
                      </div>
                    </div>
                    {file.dataUrl && (
                      <button
                        onClick={() => downloadFile(file)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition"
                      >
                        ⬇️ Download
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Upload Form */}
        {!uploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 md:p-8 border border-gray-700"
          >
            {/* Your Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Your Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Your Name</label>
                  <input
                    type="text"
                    value={uploaderName}
                    onChange={(e) => setUploaderName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 bg-gray-900/50 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Your Email *</label>
                  <input
                    type="email"
                    value={uploaderEmail}
                    onChange={(e) => setUploaderEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 bg-gray-900/50 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2 text-gray-300">Message (Optional)</label>
                <textarea
                  value={uploaderMessage}
                  onChange={(e) => setUploaderMessage(e.target.value)}
                  placeholder="Add a message for the recipient..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-900/50 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {/* File Upload Area */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Select Files</h2>
              <label className="block">
                <div className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center hover:border-blue-500 transition cursor-pointer">
                  <div className="text-5xl mb-3">📁</div>
                  <p className="text-xl font-semibold mb-2 text-gray-900">Click to select files</p>
                  <p className="text-gray-600">or drag and drop them here</p>
                  <p className="text-sm text-gray-500 mt-2">Maximum file size: 100MB</p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </label>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h3 className="font-semibold text-gray-700">Selected Files ({selectedFiles.length})</h3>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {file.type.includes('image') ? '🖼️' :
                           file.type.includes('pdf') ? '📄' :
                           file.type.includes('video') ? '🎥' : '📎'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-600">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-700 transition"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || !uploaderEmail}
              className="w-full py-4 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
            </button>
          </motion.div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Uploading Files...</h2>

            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-700">Progress</span>
                <span className="text-gray-700">{uploadedCount} of {selectedFiles.length} files</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(uploadedCount / selectedFiles.length) * 100}%` }}
                  className="h-full bg-blue-600"
                />
              </div>
            </div>

            <div className="space-y-3">
              {uploadSteps.map((step) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: step.id * 0.1 }}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    step.status === 'completed' ? 'bg-green-50' :
                    step.status === 'active' ? 'bg-blue-50' :
                    'bg-gray-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                    step.status === 'completed' ? 'bg-green-500' :
                    step.status === 'active' ? 'bg-blue-500 animate-pulse' :
                    'bg-gray-600'
                  }`}>
                    {step.status === 'completed' ? '✓' : step.id}
                  </div>
                  <span className={`${step.status === 'active' ? 'font-semibold' : ''} text-gray-900`}>
                    {step.title}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BusinessUploadPortal;